import {
  useMediaQuery,
  useTheme
} from "@mui/material";
import { Clock } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CarouselCardProps } from "./Card.types";
import { CardType } from "./CardType.component";
import {
  BoxCollapse,
  CarouselCard,
  CarouselInfo,
  CarouselMask, StatusBoxesContainer, StatusBoxesStyle, StyledDurationBadge
} from "./CarouselCard.style";
import { HiddenCTA } from "./HiddenCTA.component";
import { Info } from "./Info.component";
import { TopicTags } from "./TopicTags.component";
import { VideoCover } from "./VideoCover.component";
import { STATUS_LO } from "../../consts";
import { formatDuration, hasPassedExpiration } from "../../utils/datetime";


export function CarouselCardRestyle({
  categories,
  coverPublicURL,
  complexity,
  coverVideoPublicURL,
  disabled,
  duration,
  ecmRegistration,
  endDate,
  expirationDate,
  ecm_specialization,
  ecmType,
  isMandatory,
  isTest,
  isEditorial,
  isSurvey,
  isToj,
  learningObjectType,
  learningObjectTypology,
  setOpenModalEcm,
  small,
  startDate,
  status,
  title,
  credits,
  courseId = undefined,
  enrollType,
  grandParentId,
  id,
  isStandAlone,
  madeAttempts,
  percentageOfCompletion,
  parentId,
  rootId = undefined,
  canAccessNow,
  cardType,
  externalFileUrl,
  isFavourite = false,
  attributes = [],
  isSuggestedAI,
  isSuggestedHR,
  isSuggestedManager,
  hideProgressBar = false,
  isLinkRelative,
  ...props
}: CarouselCardProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const durationNumber = parseInt(`${duration}`);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <CarouselCard 
      role="region" 
      tabIndex={ 0 } 
      aria-label={ title } 
      $hideProgressBar={ hideProgressBar } 
      onMouseEnter={ () => setIsHovered(true) } 
      onMouseLeave={ () => setIsHovered(false) }
    >
      <StatusBoxesStyle aria-hidden="true" tabIndex={ -1 }>
        <StatusBoxesContainer
          id={ id }
          isMandatory={ isMandatory }
          endDate={ endDate }
          isCompleted={ status === "C" }
          isExpired={ hasPassedExpiration(expirationDate) }
          expirationDate={ expirationDate }
          startDate={ startDate }
          learningObjectTypology={ learningObjectTypology }
          isToj={ isToj }
          isFavourite={ isFavourite }
          attributes={ attributes }
          isSuggestedAI={ isSuggestedAI }
          isSuggestedHR={ isSuggestedHR }
          isSuggestedManager={ isSuggestedManager }
        />
      </StatusBoxesStyle>
      <VideoCover
        aria-hidden="true"
        img={ coverPublicURL }
        url={ coverVideoPublicURL }
        sx={ {
          maxHeight: "calc(100% - 7rem)",
          backgroundSize: "cover"
        } }
      />

      <div id="box img-mask" style={ {
        position: "absolute",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        width: "100%"
      } }>
        <CarouselMask tabIndex={ -1 } className="carousel-mask" />
        <CarouselInfo tabIndex={ -1 } className="carousel-info" $isMobile={ isMobile }>
          <Info
            title={ title }
            progression={ percentageOfCompletion }
            status={ status }
            learningObjectTypology={ learningObjectTypology }
            courseId={ courseId }
            id={ id }
            rootId={ rootId }
            disabled={ disabled }
            grandParentId={ grandParentId }
            externalFileUrl={ externalFileUrl }
            isLinkRelative={ isLinkRelative }
            isEditorial={ isEditorial }
            complexity={ complexity }
            sx={ { pointerEvents: setOpenModalEcm && disabled ? "none" : "auto" } }
            onClick={ () => {
            //TODO: setOpenModalEm non viene passato come prop , valutare l'eliminazione
              if (setOpenModalEcm && disabled && ecm_specialization && !ecmRegistration) {
                setOpenModalEcm(true);
              }
            } }
          />
          <div style={ { display: "flex", justifyContent: "space-between", minHeight: "2rem", paddingBottom: "0.5rem" } }>

            <CardType
              ecmType={ ecmType }
              ecm_specialization={ ecm_specialization }
              isTest={ Boolean(isTest) }
              isEditorial={ Boolean(isEditorial) }
              isSurvey={ Boolean(isSurvey) }
              learningObjectTypology={ learningObjectTypology }
              learningObjectType={ learningObjectType }
              fontSize={ "0.75rem" }
              duration={ duration }
              status={ status }
              small={ small }
            />
            { duration && !isToj && !isNaN(durationNumber) && durationNumber > 0 ?
              <StyledDurationBadge
                tabIndex={ -1 }
                width="fit-content"
                boxSizing="content-box"
                height={ "1rem" }
                border={ `1px solid ${theme.customColors.textTag}` }
                sx={ { background: `${theme.customColors.backgroundSecondary} !important` } }
                padding={ "0.109rem 0.437rem" }
                gap={ "0.1875rem" }
                $completed={ status === STATUS_LO.C }>

                <Clock size={ "0.875rem" } color={ theme.customColors.textTag } />
                <span style={ {
                  lineHeight: "normal",
                  fontSize: "0.75rem",
                  fontWeight: "600",
                  color: theme.customColors.textTag
                } }>
                  { duration && formatDuration(duration, t) }
                </span>
              </StyledDurationBadge> : null
            }
          </div>
          <BoxCollapse className="box_collapse">
            { categories && (categories.length > 0) &&
              <TopicTags
                topicTags={ categories }
                tagsCollapsed={ true }
              />
            }
            <HiddenCTA
              courseId={ courseId }
              color={ theme.customColors.exploreCardText }
              disabled={ disabled }
              ecm_specialization={ ecm_specialization }
              ecmRegistration={ ecmRegistration }
              enrollType={ enrollType }
              grandParentId={ grandParentId }
              id={ id }
              isCard={ true }
              isStandAlone={ isStandAlone }
              isSurvey={ isSurvey }
              isTest={ isTest }
              isEditorial={ Boolean(isEditorial) }
              learningObjectType={ learningObjectType }
              externalFileUrl={ externalFileUrl }
              learningObjectTypology={ learningObjectTypology }
              madeAttempts={ madeAttempts }
              parentId={ parentId }
              percentageOfCompletion={ percentageOfCompletion }
              rootId={ rootId }
              setOpenModalEcm={ setOpenModalEcm }
              status={ status }
              title={ title }
              canAccessNow={ canAccessNow }
              credits={ credits }
              showDetailsButton={ true }
              ctaPosition={ "split" }
              cardType={ cardType }
              attributes={ attributes }
              isLinkRelative={ isLinkRelative }
              isHovered={ isHovered }
              isFavourite={ !!isFavourite }
              { ...props } />
          </BoxCollapse>

        </CarouselInfo>
      </div>

    </CarouselCard>
  );
}
